import React, { FC, Fragment } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Col, Row } from 'react-styled-flexboxgrid';

import { CURRENCIES } from '@savgroup-front-common/constants';
import { AdditionalInformationDto } from '@savgroup-front-common/types';

import { CommonWorkflowService } from '../../api';
import { safeFormattedIntlString } from '../../formatters';

import { AdditionalInformationProvider } from './AdditionalInformation.context';
import {
  checkIfNextInputIsFull,
  checkIfShouldRenderWithFullWith,
} from './AdditionalInformation.helpers';
import { AdditionalInformationInput } from './AdditionalInformationInput';
import useAdditionalInformation from './hooks/useAdditionalInformation';
import messages from './messages';

interface NeededInformation extends AdditionalInformationDto {
  internalId: string;
  claimIds: string[];
  fileUploadEndpoints?: Record<
    string,
    {
      uploadEndpoint: string;
      deleteEndpoint: string;
    }
  >;
}

interface AdditionalInformationProps {
  neededInformation?: NeededInformation[];
  formContext?: UseFormReturn;
  ignoreUniquenessByProduct?: boolean;
  prefixFormName?: string;
  onAdditionalInformationSave?: () => Promise<void>;
  onNeedToUploadFileAttachements?: (payload: { files: File[] }) => void;
  isLiveUpload?: boolean;
  isNewDesign?: boolean;
  fullSizeItem?: boolean;
  isScrollIntoView?: boolean;
  hasMultiProduct?: boolean;
  suffixOptionButton?: React.ComponentType<any>;
  currency?: CURRENCIES;
  claimId?: string;
  sellerId: string;
  getQrUploadLink?:
    | typeof CommonWorkflowService.getFileAttachmentUploadLinkFromControlCommand
    | typeof CommonWorkflowService.getFileAttachmentUploadLinkFromMyAccountCommand;
  fileReference?: string;
}

const AdditionalInformation: FC<AdditionalInformationProps> = ({
  neededInformation = [],
  formContext,
  ignoreUniquenessByProduct = false,
  prefixFormName,
  onAdditionalInformationSave = () => Promise.resolve(),
  onNeedToUploadFileAttachements,
  isLiveUpload = false,
  isNewDesign = false,
  fullSizeItem = false,
  isScrollIntoView = false,
  hasMultiProduct = false,
  suffixOptionButton,
  currency,
  claimId,
  sellerId,
  getQrUploadLink = CommonWorkflowService.getFileAttachmentUploadLinkFromControlCommand,
  fileReference,
}) => {
  const { neededInformationGroupedByIdValues } = useAdditionalInformation({
    neededInformation,
    ignoreUniquenessByProduct,
  });

  return (
    <AdditionalInformationProvider
      neededInformation={neededInformation}
      prefixFormName={prefixFormName}
      isLiveUpload={isLiveUpload}
      isNewDesign={isNewDesign}
      fullSizeItem={fullSizeItem}
      isScrollIntoView={isScrollIntoView}
      hasMultiProduct={hasMultiProduct}
      suffixOptionButton={suffixOptionButton}
      currency={currency}
      claimId={claimId}
      onAdditionalInformationSave={onAdditionalInformationSave}
      onNeedToUploadFileAttachements={onNeedToUploadFileAttachements}
      sellerId={sellerId}
      getQrUploadLink={getQrUploadLink}
      fileReference={fileReference}
    >
      <Row>
        {neededInformationGroupedByIdValues.map(
          ([key, neededInformationGroup], index) => {
            return (
              <Fragment key={key}>
                {neededInformationGroup.map(
                  (neededInformationItem, subIndex) => {
                    const {
                      uniquenessByProduct = false,
                      id,
                      name = undefined,
                      isEven,
                      claimIds,
                    } = neededInformationItem;

                    if (!claimIds || claimIds.length === 0) {
                      return null;
                    }

                    const nextGroup =
                      neededInformationGroupedByIdValues[index + 1];

                    const nextInputIsFull = checkIfNextInputIsFull({
                      ignoreUniquenessByProduct,
                      nextGroup,
                    });

                    const isFull = checkIfShouldRenderWithFullWith({
                      ignoreUniquenessByProduct,
                      isEven,
                      nextInputIsFull,
                      neededInformationGroup,
                      nextGroup,
                      neededInformationItem,
                      fullSizeItem,
                    });

                    const colProps = { xs: 12, sm: isFull ? 12 : 6 };

                    return (
                      <Col
                        key={id}
                        {...colProps}
                        data-testid={`additionalInformation_${id}`}
                      >
                        <AdditionalInformationInput
                          formContext={formContext}
                          label={subIndex === 0 ? name : undefined}
                          placeholder={
                            uniquenessByProduct &&
                            neededInformationGroup.length > 1
                              ? safeFormattedIntlString(
                                  messages.productNumber,
                                  {
                                    num: subIndex + 1,
                                  },
                                )
                              : undefined
                          }
                          neededInformationItem={neededInformationItem}
                        />
                      </Col>
                    );
                  },
                )}
              </Fragment>
            );
          },
        )}
      </Row>
    </AdditionalInformationProvider>
  );
};

export default AdditionalInformation;
